/**
 * Новая подборка или общая тематика — форма окна «+ Новая подборка».
 *
 * Подборке нужно хоть что-то: название или затравка; тематике — затравка.
 * С затравкой сервис сразу ставит сбор Wordstat; подборка без затравки —
 * пустая папка, слова в неё кладут на её странице. После создания — переход
 * в неё: следующее действие человека всегда там.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";

import { DialogFooter } from "@/components/trends/Dialog";
import { btn, btnPrimary, ErrorLine, input, label, Segmented } from "@/components/trends/ui";
import { post, trendKeys } from "@/lib/trends";

type Mode = "personal" | "shared";

export function NewTopicForm({ onCancel }: { onCancel: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>("personal");
  const [phrase, setPhrase] = useState("");
  const [name, setName] = useState("");
  const personal = mode === "personal";
  const ready = personal ? Boolean(name.trim() || phrase.trim()) : Boolean(phrase.trim());

  const create = useMutation({
    mutationFn: () =>
      post<{ id: number; task_id: number | null }>("/api/trends", {
        phrase: phrase.trim(),
        name: name.trim(),
        personal,
      }),
    onSuccess: async (got) => {
      await queryClient.invalidateQueries({ queryKey: trendKeys.list });
      navigate(`/trends/${got.id}`);
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (ready && !create.isPending) create.mutate();
  };

  const nameField = (
    <label className="grid gap-1">
      <span className={label}>{personal ? "название подборки" : "название (необязательно)"}</span>
      <input
        className={`${input} w-full`}
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={personal ? "например, «Выпускные платья»" : "по умолчанию — затравка"}
        maxLength={160}
        autoFocus={personal}
      />
    </label>
  );
  const phraseField = (
    <label className="grid gap-1">
      <span className={label}>{personal ? "затравка Wordstat (необязательно)" : "затравка Wordstat"}</span>
      <input
        className={`${input} w-full`}
        value={phrase}
        onChange={(e) => setPhrase(e.target.value)}
        placeholder={personal ? "например, «выпускное платье»" : "например, «вечернее платье»"}
        maxLength={200}
        autoFocus={!personal}
      />
    </label>
  );

  return (
    <form onSubmit={submit}>
      <div className="grid gap-3 px-4 py-3">
        <Segmented
          label="что заводим"
          value={mode}
          onPick={setMode}
          options={[
            { value: "personal", label: "моя подборка", title: "Своя папка слов: править будете только вы" },
            { value: "shared", label: "общая тематика", title: "Тематика Wordstat для всех" },
          ]}
        />
        {personal ? (
          <>
            {nameField}
            {phraseField}
          </>
        ) : (
          <>
            {phraseField}
            {nameField}
          </>
        )}
        <p className="text-[11.5px] text-fg-faint">
          {phrase.trim()
            ? "С затравкой сразу встанет в очередь сбор Wordstat: фразы, из которых удобно брать слова. Идёт минуту-две."
            : "Без затравки подборка — пустая папка: слова добавите на её странице и сами запустите сбор по нужным."}
        </p>
        <ErrorLine error={create.error} />
      </div>
      <DialogFooter>
        <button type="button" className={btn} onClick={onCancel}>
          Отмена
        </button>
        <button type="submit" className={btnPrimary} disabled={!ready || create.isPending}>
          <Plus size={14} />
          {create.isPending ? "Завожу…" : phrase.trim() ? "Завести и собрать" : "Завести"}
        </button>
      </DialogFooter>
    </form>
  );
}
